<!-- src/pages/CtvRegister.vue -->
<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { api } from '../api/client'

const router = useRouter()
const form = ref({
  fullName: '',
  dobText: '',        // người dùng nhập tay: dd/mm/yyyy
  genderText: '',     // người dùng chọn/nhập: Nam | Nữ | Khác
  address: '',
  phone: '',
  cccdNumber: '',
  bankAccountNumber: '',
  bankName: ''
})
const msg = ref('')
const okMsg = ref('')
const isSubmitting = ref(false)

/* ===== Regex & helpers ===== */
const rePhone   = /^0\d{9,10}$/                // VN mobile: 0 + 9–10 số
const reCCCD    = /^\d{12}$/                   // CCCD đúng 12 số
const reTax     = /^\d{5,20}$/        // MST: 10 số; chi nhánh: 10-3
const reBankAcc = /^\d{5,20}$/                 // STK: 8–20 số

function parseDobToIso(dobText) {
  // chấp nhận d/m/yyyy hoặc dd/mm/yyyy, phân cách / hoặc -
  const s = String(dobText || '').trim().replace(/-/g,'/').replace(/\s+/g,'')
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s)
  if (!m) return { ok:false, err:'Ngày sinh không hợp lệ. Định dạng dd/mm/yyyy.' }
  let [ , dd, mm, yyyy ] = m
  const d = parseInt(dd,10), mth = parseInt(mm,10), y = parseInt(yyyy,10)
  if (y < 1900 || y > new Date().getFullYear()) return { ok:false, err:'Năm sinh không hợp lệ.' }
  // kiểm tra ngày-tháng hợp lệ
  const dt = new Date(y, mth-1, d)
  if (dt.getFullYear()!==y || dt.getMonth()!==mth-1 || dt.getDate()!==d) return { ok:false, err:'Ngày sinh không hợp lệ.' }
  if (dt > new Date()) return { ok:false, err:'Ngày sinh không được ở tương lai.' }
  // ISO yyyy-mm-dd
  const p = n=>String(n).padStart(2,'0')
  return { ok:true, iso:`${y}-${p(mth)}-${p(d)}` }
}

function normalizeGender(genderText) {
  const s = String(genderText||'').trim().toLowerCase()
    .normalize('NFKD').replace(/[\u0300-\u036f]/g,'') // bỏ dấu
  if (s.startsWith('nam')) return { ok:true, code:'MALE' }
  if (s.startsWith('nu'))  return { ok:true, code:'FEMALE' }
  if (s.startsWith('khac') || s==='other') return { ok:true, code:'OTHER' }
  return { ok:false, err:'Vui lòng chọn Giới tính: Nam, Nữ hoặc Khác.' }
}

function validateLocal(payload) {
  if (!payload.fullName || payload.fullName.length < 2) return 'Họ tên tối thiểu 2 ký tự.'

  const dob = parseDobToIso(form.value.dobText)
  if (!dob.ok) return dob.err

  const g = normalizeGender(form.value.genderText)
  if (!g.ok) return g.err

  if (!payload.address) return 'Vui lòng nhập Địa chỉ thường trú.'

  if (!payload.phone) return 'Vui lòng nhập Số điện thoại.'
  if (!rePhone.test(payload.phone)) return 'Số điện thoại không hợp lệ (0 + 9–10 số).'


  if (!payload.cccdNumber) return 'Vui lòng nhập số CCCD.'
  if (!reCCCD.test(payload.cccdNumber)) return 'CCCD phải đúng 12 chữ số.'


  if (!payload.bankAccountNumber) return 'Vui lòng nhập Số tài khoản ngân hàng.'
  if (!reBankAcc.test(payload.bankAccountNumber))
    return 'Số tài khoản gồm 8–20 chữ số.'

  if (!payload.bankName || payload.bankName.trim().length < 2)
    return 'Tên ngân hàng tối thiểu 2 ký tự.'

  return '' // OK
}

async function submit(e) {
  e.preventDefault()
  if (isSubmitting.value) return
  msg.value = ''
  okMsg.value = ''

  // chuẩn hoá & trim
  const base = Object.fromEntries(
    Object.entries(form.value).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
  )

  // dob & gender chuẩn hóa cho API
  const dob = parseDobToIso(base.dobText)
  const g = normalizeGender(base.genderText)
  const payload = {
    fullName: base.fullName,
    dob: dob.ok ? dob.iso : '',             // API nhận dob dạng YYYY-MM-DD
    gender: g.ok ? g.code : '',             // API nhận MALE | FEMALE | OTHER
    address: base.address,
    phone: base.phone,
    cccdNumber: base.cccdNumber,
    bankAccountNumber: base.bankAccountNumber,
    bankName: base.bankName
  }

  const vErr = validateLocal(payload)
  if (vErr) { msg.value = vErr; return }

  isSubmitting.value = true
  try {
    const { data } = await api.post('/ctv/register', payload)
    if (data?.ok) {
      okMsg.value = 'Đã gửi hồ sơ. Vui lòng chờ admin duyệt (bạn sẽ được mời ký online).'
      setTimeout(() => router.push('/dashboard'), 900)
    }
  } catch (err) {
    const m = err?.response?.data?.error || 'REGISTER_FAILED'
    const map = {
      DUPLICATE_PHONE: 'Số điện thoại đã được sử dụng.',
      DUPLICATE_CCCDNUMBER: 'Số CCCD đã tồn tại.',
      CTV_EXISTS: 'Bạn đã có hồ sơ CTV.'
    }
    msg.value = map[m] || `Gửi hồ sơ thất bại.${m && m!=='REGISTER_FAILED' ? ' ('+m+')' : ''}`
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="main">
    <div class="card card-scroll" role="form" aria-label="Form đăng ký CTV">
      <h2 class="title">Đăng ký Cộng tác viên</h2>

      <form class="form" @submit="submit" novalidate>
        <!-- 1. Họ tên -->
        <input
          class="input"
          placeholder="Họ và tên *"
          autocomplete="name"
          required
          minlength="2"
          autofocus
          v-model="form.fullName"
          :disabled="isSubmitting"
          aria-label="Họ và tên"
        />

        <!-- 2. Ngày sinh (nhập tay) -->
        <input
          class="input"
          placeholder="Ngày sinh (dd/mm/yyyy) *"
          inputmode="numeric"
          v-model="form.dobText"
          :disabled="isSubmitting"
          aria-label="Ngày sinh (dd/mm/yyyy)"
        />

        <!-- 3. Giới tính (datalist giống ngân hàng) -->
        <input
          class="input"
          placeholder="Giới tính (Nam / Nữ / Khác) *"
          list="genders"
          v-model="form.genderText"
          :disabled="isSubmitting"
          autocomplete="sex"
          aria-label="Giới tính"
        />
        <datalist id="genders">
          <option value="Nam" />
          <option value="Nữ" />
          <option value="Khác" />
        </datalist>

        <!-- 4. Địa chỉ -->
        <input
          class="input"
          placeholder="Địa chỉ thường trú *"
          autocomplete="street-address"
          required
          v-model="form.address"
          :disabled="isSubmitting"
          aria-label="Địa chỉ thường trú"
        />

        <!-- 5. Điện thoại -->
        <input
          class="input"
          placeholder="Số điện thoại *"
          autocomplete="tel"
          inputmode="numeric"
          pattern="^0\\d{9,10}$"
          title="Nhập số di động Việt Nam bắt đầu bằng 0, 10–11 ký tự số."
          required
          v-model="form.phone"
          :disabled="isSubmitting"
          aria-label="Số điện thoại"
        />

     

        <!-- 7. CCCD -->
        <input
          class="input"
          placeholder="Số CCCD *"
          inputmode="numeric"
          pattern="^\\d{12}$"
          title="CCCD gồm đúng 12 chữ số."
          required
          v-model="form.cccdNumber"
          :disabled="isSubmitting"
          aria-label="Số CCCD"
        />


        <!-- 9. Số tài khoản -->
        <input
          class="input"
          placeholder="Số tài khoản ngân hàng *"
          inputmode="numeric"
          pattern="^\\d{5,20}$"
          title="Số tài khoản gồm 5–20 chữ số."
          required
          v-model="form.bankAccountNumber"
          :disabled="isSubmitting"
          autocomplete="off"
          aria-label="Số tài khoản ngân hàng"
        />

        <!-- 10. Tên ngân hàng -->
        <input
          class="input"
          placeholder="Tên ngân hàng (ví dụ: Vietcombank) *"
          list="banks"
          required
          minlength="2"
          v-model="form.bankName"
          :disabled="isSubmitting"
          autocomplete="organization"
          aria-label="Tên ngân hàng"
        />
        <datalist id="banks">
          <option value="Vietcombank" />
          <option value="VietinBank" />
          <option value="BIDV" />
          <option value="Agribank" />
          <option value="Techcombank" />
          <option value="MB Bank" />
          <option value="ACB" />
          <option value="Sacombank" />
          <option value="TPBank" />
          <option value="VPBank" />
          <option value="VIB" />
          <option value="HDBank" />
          <option value="SHB" />
          <option value="Eximbank" />
        </datalist>

        <div v-if="msg" class="error">{{ msg }}</div>
        <div v-if="okMsg" class="note ok">{{ okMsg }}</div>

        <div class="actions">
          <button class="btn" type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? 'Đang gửi…' : 'Gửi hồ sơ' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.main{
  min-height:100dvh;
  display:grid;
  place-items:center;
  padding:20px;
}
.card{
  width:min(720px,100%);
  background:#0f1930;
  border:1px solid #24365f;
  border-radius:12px;
  padding:20px;
}
.card-scroll{
  /* Cho phép cuộn mượt như trang hợp đồng */
  max-height:90dvh;
  overflow:auto;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
}
.title{ text-align:center; margin:0 0 12px }
.form{ display:grid; gap:12px }
.input{
  width:100%;
  padding:12px 14px;
  border:1px solid #24365f;
  border-radius:8px;
  background:#0b1220;
  color:#e8eefc;
}
.input:disabled{ opacity:.7 }
.error{ margin-top:4px; color:#ff6b6b; text-align:center }
.ok{ color:#34d399; text-align:center }
.actions{ display:flex; justify-content:center; margin-top:6px }
.btn{
  padding:10px 16px;
  border:0; border-radius:8px;
  background:#2dd4bf; color:#0b1220; font-weight:600;
  cursor:pointer;
}
.btn:disabled{ opacity:.7; cursor:not-allowed }
@media (max-width:640px){
  .card{ padding:16px }
}
</style>
  